import { Button, Stack } from "@mantine/core";
import clsx from "clsx";
import { type FC, useEffect, useState } from "react";

import startStyles from "./TrainingCallStartInterstitial.module.css";
import styles from "./TrainingEvaluationInterstitial.module.css";

const LOADING_DOTS = [".", "..", "..."] as const;
const LOADING_DOT_INTERVAL_MS = 500;

/** Animated dots for the primary CTA loading label. */
const LoadingDotsLabel: FC<{ label: string }> = ({ label }) => {
    const [dotIndex, setDotIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setDotIndex((current) => (current + 1) % LOADING_DOTS.length);
        }, LOADING_DOT_INTERVAL_MS);
        return () => clearInterval(timer);
    }, []);

    return (
        <>
            {label}
            <span className={startStyles.preparingDots} aria-hidden="true">
                {LOADING_DOTS[dotIndex]}
            </span>
        </>
    );
};

export interface TrainingCallStartInterstitialProps {
    agentReady: boolean;
    hasError: boolean;
    /** Keeps this screen mounted after Start until the task is accepted. */
    isJoiningCall?: boolean;
    onStartCall: () => void;
    onRetry: () => void;
    onCancel: () => void;
}

/** Pre-call interstitial; mirrors {@link TrainingEvaluationInterstitial}. */
export const TrainingCallStartInterstitial: FC<TrainingCallStartInterstitialProps> = ({
    agentReady,
    hasError,
    isJoiningCall = false,
    onStartCall,
    onRetry,
    onCancel,
}) => {
    const isPrimaryLoading = isJoiningCall || !agentReady;

    return (
        <div className={styles.overlay} data-testid="call-start-interstitial" role="dialog" aria-modal="true">
            <div className={clsx(styles.interim, startStyles.layout)}>
                <div className={styles.ringWrap} aria-hidden="true">
                    <div className={styles.ringHalo} />
                    <svg width="132" height="132" viewBox="0 0 132 132">
                        <defs>
                            <linearGradient id="callStartGrad" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#8223d2" />
                                <stop offset="100%" stopColor="#a45be8" />
                            </linearGradient>
                        </defs>
                        <circle className={styles.ringTrack} cx="66" cy="66" r="60" />
                        <circle className={startStyles.ringFill} cx="66" cy="66" r="60" />
                        <circle className={styles.ringArc} cx="66" cy="66" r="60" />
                        <circle className={styles.ringArc2} cx="66" cy="66" r="60" />
                    </svg>
                    <div className={styles.ringGlyph}>
                        <div className={styles.wave}>
                            <span />
                            <span />
                            <span />
                            <span />
                            <span />
                        </div>
                    </div>
                </div>

                <Stack gap="md" align="center" className={startStyles.actions}>
                    {hasError ? (
                        <Button
                            className={startStyles.startButton}
                            color="var(--mantine-color-brand-5)"
                            size="md"
                            onClick={onRetry}
                            data-testid="call-start-retry-button"
                        >
                            Retry
                        </Button>
                    ) : (
                        <Button
                            className={clsx(
                                startStyles.startButton,
                                isPrimaryLoading && startStyles.startButtonLoading,
                            )}
                            color="var(--mantine-color-brand-5)"
                            size="md"
                            disabled={isPrimaryLoading}
                            aria-busy={isPrimaryLoading}
                            aria-label={isJoiningCall ? "Starting call" : agentReady ? "Start" : "Preparing simulation"}
                            onClick={onStartCall}
                            data-testid="call-start-button"
                        >
                            {isJoiningCall ? (
                                <LoadingDotsLabel label="Starting call" />
                            ) : agentReady ? (
                                "Start"
                            ) : (
                                <LoadingDotsLabel label="Preparing simulation" />
                            )}
                        </Button>
                    )}
                    <Button
                        variant="subtle"
                        color="gray"
                        size="md"
                        onClick={onCancel}
                        disabled={isJoiningCall}
                        data-testid="call-start-cancel-button"
                    >
                        Cancel
                    </Button>
                </Stack>
                {hasError && (
                    <p className={startStyles.errorNote} aria-live="polite">
                        Something went wrong starting the simulation. Retry or cancel to go back.
                    </p>
                )}
            </div>
        </div>
    );
};

export default TrainingCallStartInterstitial;
